import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { api, apiUrl } from '../../lib/api';

/**
 * A sponsorship, where there is one.
 *
 * ## It draws nothing when there is nothing
 *
 * Which is most of the time, and is the single most important thing about this
 * component. No box, no border, no "advertise here", no reserved height that
 * collapses a second later. A slot that takes up room while empty is the
 * complaint people actually have about advertising on a website, and it is
 * entirely avoidable by returning null.
 *
 * ## Counted when it is seen, not when it is sent
 *
 * Being served is not being seen: a card at the bottom of the library rail on a
 * screen scrolled to the top was fetched and never looked at, and counting that
 * would be selling something that did not happen. So the impression is reported
 * from here, after half the card has been on the screen for a second, which is
 * the industry's definition and the one an advertiser already knows.
 *
 * Once per campaign per mount. The server caps it again per reader per day, on
 * the sound assumption that anything a browser reports is something a browser
 * can be made to lie about.
 *
 * ## The click goes through our API, but not always through the browser
 *
 * A card pointing off the site is an ordinary anchor to `click_path`, which
 * redirects: countable, and the destination stays editable without a deploy.
 * New tab, `sponsored` for the tag, `noopener` because the page on the other
 * end is not ours.
 *
 * A card pointing at one of our own pages is a router link instead, and the
 * count goes by `fetch`. Following the redirect there would be a document load
 * - the whole application torn down and rebuilt, stopping whatever was playing
 * - which is an absurd thing to do to somebody on a card whose entire pitch is
 * "upload something to this site". Same counter either way.
 *
 * ## No flag check here
 *
 * The server answers with no campaign while the feature is off. Checking a flag
 * in the client would be checking the *signed-in* reader's flags, and half the
 * readers of a live room are not signed in - so it would show ads to strangers
 * and hide them from members, which is backwards.
 */

/** The three places that have one. Mirrors `AdRepository::SLOTS`. */
export type AdSlotName = 'dashboard-left' | 'dashboard-feed' | 'room-rail';

interface Ad {
  id: number;
  slot: AdSlotName;
  advertiser: string;
  image: string | null;
  alt: string | null;
  headline: string | null;
  body: string | null;
  cta: string | null;
  click_path: string;
  /** A path on this site to route to, or null for anywhere else. */
  to: string | null;
  label: string;
}

/** Half of it on screen, for a second. The IAB's display standard. */
const VIEWABLE_FRACTION = 0.5;
const VIEWABLE_MS = 1000;

export function AdSlot({ slot, className = '' }: { slot: AdSlotName; className?: string }) {
  const box = useRef<HTMLDivElement | null>(null);

  /**
   * The campaign this mount has already reported.
   *
   * A ref rather than state: counting must not re-render anything, and the
   * observer's callback closes over the render it was created in.
   */
  const counted = useRef<number | null>(null);

  const { data } = useQuery({
    queryKey: ['ad', slot],
    queryFn: () => api.get<{ ad: Ad | null }>('/ads', { query: { slot } }),
    /*
     * Long, and not refetched on focus.
     *
     * The alternative is a fresh campaign every time somebody alt-tabs back,
     * which is a request per focus for a card nobody asked to change - and
     * would make the impression count a measure of window switching.
     */
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const ad = data?.ad ?? null;
  const id = ad?.id ?? null;

  useEffect(() => {
    const node = box.current;

    if (node === null || id === null) return;

    let timer: number | undefined;

    const stop = () => {
      if (timer !== undefined) {
        window.clearTimeout(timer);
        timer = undefined;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        // A background tab reports its elements as intersecting, because the
        // layout has not changed - only nobody is looking at it.
        const visible =
          entry !== undefined &&
          entry.isIntersecting &&
          entry.intersectionRatio >= VIEWABLE_FRACTION &&
          document.visibilityState === 'visible';

        if (!visible) {
          stop();

          return;
        }

        if (timer !== undefined || counted.current === id) return;

        timer = window.setTimeout(() => {
          timer = undefined;
          counted.current = id;

          // Nothing depends on the answer. A failed count is a lost impression,
          // which is the right way for this to fail - the alternative is
          // retrying until it sticks, which is how a counter becomes fiction.
          void api.post(`/ads/${id}/impression`, { slot }).catch(() => undefined);
        }, VIEWABLE_MS);
      },
      { threshold: [0, VIEWABLE_FRACTION] },
    );

    observer.observe(node);

    return () => {
      observer.disconnect();
      stop();
    };
  }, [id, slot]);

  if (ad === null) return null;

  const wide = slot === 'dashboard-feed';

  const surface = `group block overflow-hidden rounded-[var(--radius-card)] border border-line bg-raised transition-colors hover:border-line-strong ${
    wide ? 'sm:flex sm:items-stretch' : ''
  }`;

  const inside = (
    <>
      {ad.image !== null && (
        <img
          src={ad.image}
          alt={ad.alt ?? ''}
          loading="lazy"
          className={
            wide
              ? 'h-32 w-full object-cover sm:h-auto sm:w-[42%] sm:shrink-0'
              : 'h-auto w-full object-cover'
          }
        />
      )}

      <div className="flex flex-col gap-1.5 p-3.5">
        {/*
          The disclosure, first and in words.

          Not on hover, not in a corner of the image, and not dependent on the
          creative having remembered to include it: the reader has to be able
          to tell that this is an ad and whose it is before they read anything
          else on the card.
        */}
        <span className="meta-caps text-faint" data-ad-label>
          {ad.label} · {ad.advertiser}
        </span>

        {ad.headline !== null && (
          <span className="text-sm font-medium leading-snug text-ink">{ad.headline}</span>
        )}

        {ad.body !== null && (
          <span className="text-xs leading-relaxed text-soft">{ad.body}</span>
        )}

        {ad.cta !== null && (
          <span className="meta-caps mt-1 text-accent group-hover:underline">{ad.cta} →</span>
        )}
      </div>
    </>
  );

  return (
    <div ref={box} className={className} data-ad-slot={slot} data-ad-id={ad.id}>
      {ad.to !== null ? (
        <Link
          to={ad.to}
          data-ad-link
          className={surface}
          onClick={() => {
            /*
             * Counted on the way, not before it.
             *
             * Nothing waits for this and nothing acts on the answer: the
             * navigation is a router transition rather than a page load, so
             * the request outlives the click on its own and a failed count is
             * a lost click - which is the right way for a counter to fail.
             */
            void api.post(ad.click_path).catch(() => undefined);
          }}
        >
          {inside}
        </Link>
      ) : (
        <a
          href={apiUrl(ad.click_path)}
          target="_blank"
          // `sponsored` is the tag search engines ask for on a paid link, and
          // `noopener` because the page on the other end is not ours.
          rel="sponsored noopener noreferrer"
          data-ad-link
          className={surface}
        >
          {inside}
        </a>
      )}
    </div>
  );
}
